import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import type {
  CloneOrderMode,
  CloneOrderProvider,
  CloneOrderRequest,
  CloneOrderResponse,
  FulfillmentOrderLine,
  FulfillmentReservation,
} from '@/infrastructure/FulfillmentAdminClient';
import { serverRefusal } from '@/utils/fulfillmentErrors';
import { ROUTES } from '@/routes/routes';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';
import { withCarrierExplanation } from '@/utils/carrierErrorText';

/** What the dialog asks for; the page adds the idempotency key, which belongs to the attempt, not the form. */
export type CloneOrderDraft = Omit<CloneOrderRequest, 'idempotencyKey'>;

interface CloneOrderModalProps {
  readonly show: boolean;
  readonly orderId: number;
  readonly lines: FulfillmentOrderLine[];
  readonly reservations?: FulfillmentReservation[];
  readonly currency: string;
  readonly onHide: () => void;
  /** Resolves with the server's answer; a rejection stays in the dialog so the operator can retry. */
  readonly onSubmit: (draft: CloneOrderDraft) => Promise<CloneOrderResponse>;
  /** Fulfillment's Order Detail opens it in the Admin Ultimate Design; the OMS screen keeps Bootstrap's look (SSD-7441). */
  readonly ultimate?: boolean;
}

/** The OMS clone offered these three; FedEx cannot take a clone. */
const PROVIDERS: Array<{ value: CloneOrderProvider; label: string }> = [
  { value: 'DELEO', label: 'DELEO' },
  { value: 'YUN_EXPRESS', label: 'YUN EXPRESS' },
  { value: 'PARXL', label: 'PARXL' },
];

const REASON_MAX_LENGTH = 500;

/** The most lines one clone can carry, the cap the OMS clone had and the server still enforces. */
const MAX_CLONE_LINES = 27;

const TITLE_ID = 'clone-order-title';

/** The statuses a line can still be edited in, which are the only ones a split may take units from. */
const SPLITTABLE_LINE_STATUSES = ['ORDERED', 'RESERVED', 'AWAITING_INBOUND', 'INBOUNDED', 'READY'];

const CLONE_FAILED = 'The clone failed. Review the order before retrying.';

/** Why a line takes no quantity in this mode, or an empty string when it does. */
const describeLineBlock = (line: FulfillmentOrderLine, mode: CloneOrderMode): string => {
  if (mode === 'SPLIT' && line.lineStatus === 'CANCELLED') {
    return 'Cancelled';
  }
  return mode === 'SPLIT' && !SPLITTABLE_LINE_STATUSES.includes(line.lineStatus)
    ? `${line.lineStatus} — cannot split`
    : '';
};

/** A copy preselects live lines; cancelled lines are opt-in. A split starts from nothing moved. */
const defaultQuantities = (lines: FulfillmentOrderLine[], mode: CloneOrderMode): Record<number, string> =>
  Object.fromEntries(
    lines.map((line) => [
      line.lineNo,
      mode === 'COPY' && line.lineStatus !== 'CANCELLED' ? String(line.totalQuantity) : '0',
    ]),
  );

/**
 * Whether the line records the unit price the server declares for a clone: `price`, else `salePrice`, else
 * `regularPrice` on its snapshot, as BuildCloneOrderCommandHandler reads it. Product Cost (agentTracking) is not one.
 * A response without the raw snapshot cannot tell, so it is left to the server.
 */
const hasRecordedPrice = (line: FulfillmentOrderLine): boolean => {
  if (line.snapshot === undefined) {
    return true;
  }
  const snapshot = line.snapshot && typeof line.snapshot === 'object' ? (line.snapshot as Record<string, unknown>) : {};
  return [snapshot['price'], snapshot['salePrice'], snapshot['regularPrice']].some(
    (value) =>
      (typeof value === 'number' && Number.isFinite(value)) ||
      (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))),
  );
};

/** The line's share of its own total, rounded the way the server splits it; null when the line has no price. */
const shareOf = (line: FulfillmentOrderLine, qty: number): number | null =>
  line.total == null || line.totalQuantity <= 0
    ? null
    : Math.round(((line.total * qty) / line.totalQuantity) * 100) / 100;

const money = (amount: number, currency: string): string => `${currency} ${amount.toFixed(2)}`;

/**
 * Clones an order into a new internal-band order (SSD-7061), the Fulfillment 2.0 copy of the OMS clone.
 * Every refusal is named on the footer, so the disabled button is never silent about why.
 */
export function CloneOrderModal({
  show,
  orderId,
  lines,
  reservations = [],
  currency,
  onHide,
  onSubmit,
  ultimate = false,
}: CloneOrderModalProps) {
  const look = ultimate ? ULTIMATE_MODAL : {};
  const [mode, setMode] = useState<CloneOrderMode>('COPY');
  const [quantities, setQuantities] = useState<Record<number, string>>({});
  const [provider, setProvider] = useState<CloneOrderProvider | ''>('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CloneOrderResponse | null>(null);

  // A dialog opened again is a new clone, not a retry of the last one.
  useEffect(() => {
    if (show) {
      setMode('COPY');
      setQuantities(defaultQuantities(lines, 'COPY'));
      setProvider('');
      setReason('');
      setSubmitting(false);
      setError(null);
      setResult(null);
    }
    // Only opening the dialog resets it; a reload of the order underneath must not wipe what was typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const chooseMode = (next: CloneOrderMode) => {
    setMode(next);
    setQuantities(defaultQuantities(lines, next));
    setError(null);
  };

  const qtyOf = (line: FulfillmentOrderLine): number => Number(quantities[line.lineNo] ?? '0');
  const outOfRange = lines.filter((line) => {
    const qty = qtyOf(line);
    const max = describeLineBlock(line, mode) === '' ? line.totalQuantity : 0;
    return !Number.isInteger(qty) || qty < 0 || qty > max;
  });
  const picked = lines.filter((line) => describeLineBlock(line, mode) === '' && qtyOf(line) > 0);
  const copyingOnHand =
    mode === 'COPY' &&
    reservations.some(
      (reservation) =>
        reservation.kind === 'ON_HAND' &&
        reservation.state === 'ACTIVE' &&
        reservation.qty > 0 &&
        picked.some((line) => line.lineNo === reservation.lineNo),
    );
  const totalQty = picked.reduce((sum, line) => sum + qtyOf(line), 0);
  const liveQty = lines
    .filter((line) => line.lineStatus !== 'CANCELLED')
    .reduce((sum, line) => sum + line.totalQuantity, 0);
  const shares = picked.map((line) => shareOf(line, qtyOf(line)));
  const pricedTotal = shares.reduce<number>((sum, share) => sum + (share ?? 0), 0);

  const describeBlock = (): string => {
    if (outOfRange.length > 0) {
      return `Line ${outOfRange.map((line) => line.lineNo).join(', ')}: enter a whole number within the line quantity`;
    }
    if (totalQty < 1) {
      return mode === 'SPLIT' ? 'Pick at least one unit to move' : 'Pick at least one unit to clone';
    }
    const unpriced = picked.filter((line) => !hasRecordedPrice(line));
    if (unpriced.length > 0) {
      const named = unpriced.map((line) => `Line ${line.lineNo} (${line.sku})`).join(', ');
      return `${named} ${unpriced.length > 1 ? 'have' : 'has'} no recorded price; Product Cost does not set it. Ask engineering to repair the line`;
    }
    if (picked.length > MAX_CLONE_LINES) {
      return `A clone can take at most ${MAX_CLONE_LINES} lines; set the rest to 0`;
    }
    if (mode === 'SPLIT' && totalQty >= liveQty) {
      return 'A split must leave at least one unit on this order';
    }
    if (!provider) {
      return 'Pick a shipping provider';
    }
    return reason.trim() ? '' : 'Enter a reason';
  };
  const block = describeBlock();

  const submit = async () => {
    if (block !== '' || submitting || !provider) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const answer = await onSubmit({
        mode,
        lines: picked.map((line) => ({ lineNo: line.lineNo, qty: qtyOf(line) })),
        provider,
        reason: reason.trim(),
      });
      setResult(answer);
    } catch (err) {
      setError(serverRefusal(err) ?? (err instanceof Error && err.message ? err.message : CLONE_FAILED));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    if (!submitting) {
      onHide();
    }
  };

  if (result) {
    const cloneHref = ROUTES.fulfillment.orderDetail(result.cloneOrderId);
    return (
      <Modal {...look} show={show} onHide={close} size="lg" aria-labelledby={TITLE_ID}>
        <Modal.Header closeButton>
          <Modal.Title as="h5" id={TITLE_ID}>
            Clone Order
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="success" className="mb-3">
            {result.created ? 'Clone created: ' : 'This request had already created clone '}
            <Link to={cloneHref} onClick={onHide}>
              #{result.cloneOrderId}
            </Link>
            {mode === 'SPLIT' ? `, with ${totalQty} unit(s) moved off order #${orderId}.` : '.'}
          </Alert>
          {result.carrier && (
            <div className="mb-2">
              Carrier order booked — {result.carrier.carrier}, tracking {result.carrier.trackingNumber}.
            </div>
          )}
          {result.carrierError && (
            <Alert variant="warning" className="mb-0">
              Carrier label failed: {withCarrierExplanation(result.carrierError.message)}. The clone was created, but
              cannot be outbounded without a carrier label. The clone&apos;s page keeps this reason. Fix what it names,
              then use Create Carrier Order on the clone, which offers the provider picked here.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Link className="btn btn-primary btn-sm" to={cloneHref} onClick={onHide}>
            Open clone #{result.cloneOrderId}
          </Link>
          <button type="button" className="btn btn-line btn-sm" onClick={onHide}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal
      {...look}
      show={show}
      onHide={close}
      size="lg"
      backdrop={submitting ? 'static' : true}
      aria-labelledby={TITLE_ID}
    >
      <Modal.Header closeButton={!submitting}>
        <Modal.Title as="h5" id={TITLE_ID}>
          Clone Order #{orderId}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Mode *</Form.Label>
          <Form.Check
            type="radio"
            id="clone-mode-copy"
            name="clone-mode"
            label="Copy — this order stays as it is; the clone reserves its own stock (reship, replacement)"
            checked={mode === 'COPY'}
            disabled={submitting}
            onChange={() => chooseMode('COPY')}
          />
          <Form.Check
            type="radio"
            id="clone-mode-split"
            name="clone-mode"
            label="Split — move the quantities below off this order, with their reservations and amount (split shipment)"
            checked={mode === 'SPLIT'}
            disabled={submitting}
            onChange={() => chooseMode('SPLIT')}
          />
        </Form.Group>

        <Alert variant="warning">
          {mode === 'SPLIT'
            ? `The units below leave order #${orderId}. The clone gets a new order number, then its carrier order is booked with the provider picked below; if the carrier refuses it, the clone stays without one.`
            : 'The clone gets a new order number outside WooCommerce, then its carrier order is booked with the provider picked below; if the carrier refuses it, the clone stays without one.'}
        </Alert>
        {copyingOnHand && (
          <Alert variant="warning">
            Selected source lines hold ON_HAND stock. Use Split to split a shipment; Copy is for a replacement that
            needs new goods. Copy reserves new stock and leaves the source reservations in place.
          </Alert>
        )}
        {error && (
          <Alert variant="danger">
            {error}
            <div className="small mt-1">
              Retrying sends the same request again, so a clone that did land is not made twice.
            </div>
          </Alert>
        )}

        <Form.Label>{mode === 'SPLIT' ? 'Quantity to move *' : 'Quantity to clone *'}</Form.Label>
        <Table bordered responsive size="sm">
          <thead>
            <tr>
              <th>Line</th>
              <th>SKU</th>
              <th>Product</th>
              <th style={{ textAlign: 'right' }}>Qty</th>
              <th style={{ width: 140 }}>{mode === 'SPLIT' ? 'Move Qty' : 'Clone Qty'}</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => {
              const lineBlock = describeLineBlock(line, mode);
              const share = lineBlock === '' ? shareOf(line, qtyOf(line)) : null;
              return (
                <tr key={line.lineNo}>
                  <td>{line.lineNo}</td>
                  <td className="num">{line.sku}</td>
                  <td>{line.productName ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>{line.totalQuantity}</td>
                  <td>
                    {lineBlock ? (
                      <span className="blocked-note">{lineBlock}</span>
                    ) : (
                      <Form.Control
                        aria-label={`${mode === 'SPLIT' ? 'Move' : 'Clone'} quantity for line ${line.lineNo}`}
                        type="number"
                        size="sm"
                        min={0}
                        max={line.totalQuantity}
                        step={1}
                        value={quantities[line.lineNo] ?? '0'}
                        disabled={submitting}
                        isInvalid={outOfRange.includes(line)}
                        onChange={(event) =>
                          setQuantities((current) => ({ ...current, [line.lineNo]: event.target.value }))
                        }
                      />
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>{share == null ? '—' : money(share, currency)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <div className="mb-3 small" data-testid="clone-summary">
          {totalQty} unit(s) · {money(pricedTotal, currency)}
          {shares.some((share) => share == null) ? ' (some lines have no price)' : ''}
        </div>

        <Form.Group className="mb-3" controlId="clone-provider">
          <Form.Label>Shipping Provider *</Form.Label>
          <Form.Select
            value={provider}
            disabled={submitting}
            onChange={(event) => setProvider(event.target.value as CloneOrderProvider | '')}
          >
            <option value="">Select a provider</option>
            {PROVIDERS.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group controlId="clone-reason">
          <Form.Label>Reason *</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={REASON_MAX_LENGTH}
            value={reason}
            disabled={submitting}
            placeholder="Why this order is being cloned"
            onChange={(event) => setReason(event.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <span className="blocked-note">{submitting ? '' : block}</span>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-line btn-sm" disabled={submitting} onClick={close}>
            Cancel
          </button>
          <Button size="sm" disabled={submitting || block !== ''} title={block} onClick={() => void submit()}>
            {submitting
              ? 'Creating clone and carrier order…'
              : mode === 'SPLIT'
                ? `Split ${totalQty} unit(s) into a new order`
                : `Clone ${totalQty} unit(s)`}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
